// Putting a dropped object where the user wants it, with the physics stopped.
//
// A robot arrives configured -- the panel is answered before anything is built,
// because a joint has to know its actuator at construction -- but an object has
// nothing that has to be decided in advance. So it is added to the scene first
// and answered for afterwards, in front of the user: the simulation is frozen,
// the object is drawn where it landed, and a three.js gizmo drags it to where it
// belongs. Confirming is what starts the physics again.
//
// Two consequences of doing it that way, and both are the point of it:
//
//   - What is being dragged is the object itself, not a preview of it. The mesh
//     on screen is the one `addArticulatedVisuals` made for the body in the
//     `Hybrid`, drawn in the same colours off the same `.mtl`, so there is
//     nothing that can look right here and wrong once it is placed.
//   - Every drag writes straight back through `AddedObject.setPose`, so wasm and
//     the screen never hold different answers -- and "Place" has nothing left to
//     do but let the clock start.
//
// The gizmo is visible in a robot's camera view while it is up, since that view
// draws the same scene and the sim being frozen does not stop it redrawing. It
// goes when the placement does.

import { AddedObject, InterfaceHybrid, materialOptions } from "simulator";
import { Simulator } from "gorilla-physics-ui";
// The `.js` is not decoration: three ships an `exports` map, and under
// `moduleResolution: "bundler"` an extensionless subpath through it resolves to
// a file that is not there, so tsc finds neither the module nor @types/three's
// declaration beside it. With the extension both halves line up -- rspack takes
// the JS, tsc takes the `.d.ts` next to it.
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";

function element<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) {
    throw new Error(`index.html has no #${id}`);
  }
  return found as T;
}

// The control goes inside its label, as in the robot panel: these are built here
// and have no id for a `for` attribute to point at.
function labelledField(text: string, control: HTMLElement): HTMLLabelElement {
  const label = document.createElement("label");
  label.append(text, control);
  return label;
}

// Grams below a kilogram, kilograms above it. An object dropped into this scene
// is usually a printed part, which is tens of grams.
function massText(kilograms: number): string {
  return kilograms < 1
    ? `${(kilograms * 1000).toFixed(1)} g`
    : `${kilograms.toFixed(3)} kg`;
}

// Ask what the object is made of and where it goes, with the simulation stopped
// for the duration. Resolves true once the user places it, false if they discard
// it -- in which case the caller takes it back out of the scene.
//
// The caller pauses and unpauses: the freeze has to start when the drop is read
// and cover the build as well as this panel, so it is not this function's to
// own.
export function placeObject(
  simulator: Simulator,
  hybrid: InterfaceHybrid,
  object: AddedObject,
): Promise<boolean> {
  const panel = element<HTMLDivElement>("object-place");
  const note = element<HTMLDivElement>("object-place-note");
  const materialField = element<HTMLDivElement>("object-material");
  const massField = element<HTMLDivElement>("object-mass");
  const move = element<HTMLButtonElement>("object-move");
  const rotate = element<HTMLButtonElement>("object-rotate");
  const place = element<HTMLButtonElement>("object-place-add");
  const discard = element<HTMLButtonElement>("object-place-cancel");

  element("object-place-title").textContent = `Place ${object.name}`;
  note.textContent =
    "The simulation is paused. Drag the arrows to move it and the rings to " +
    "turn it, then place it.";

  // What it is made of, in the two forms that answer is available in: a name
  // out of the table, or the numbers behind one. Both write to the same place,
  // so picking a material fills the boxes in and typing in either clears the
  // name.
  //
  // The two numbers are what a material is here. Density decides what the
  // object weighs; friction decides what it does once it has landed. Friction
  // belongs to a pair of surfaces and the number here is one surface's, so
  // against the ground it is the whole answer -- a halfspace has no material of
  // its own -- while against another body the engine pairs it with that body's
  // as their geometric mean. A robot's links name none and so fall to the
  // scene's 1.0, which is why the same object grips a link harder than the
  // floor.
  const material = document.createElement("select");
  material.append(
    new Option("Custom", ""),
    ...materialOptions().map((name) => new Option(name, name)),
  );
  material.value = object.material ?? "";

  const density = document.createElement("input");
  density.type = "number";
  density.min = "1";
  density.step = "any";
  density.value = object.density.toFixed(0);

  const friction = document.createElement("input");
  friction.type = "number";
  friction.min = "0";
  friction.step = "0.05";
  friction.value = object.friction.toFixed(2);
  // The label is one word because the number is one surface's. What it makes
  // with the surface it meets is the sentence that does not fit beside a box.
  friction.title =
    "What this surface slides at on its own. Against the ground that is the " +
    "coefficient; against another body it is paired with that body's own as " +
    "their geometric mean.";

  materialField.replaceChildren(
    labelledField("Material", material),
    labelledField("Density (kg/m³)", density),
    labelledField("Friction", friction),
  );

  // The volume is the mesh's and never changes; the mass is the one number the
  // controls above move, so it is shown rather than left to the console.
  const showMass = () => {
    massField.textContent = `${(object.volume * 1e6).toFixed(1)} cm³ · ${massText(object.mass)}`;
  };
  showMass();

  const onMaterial = () => {
    if (material.value === "") {
      return;
    }
    object.setMaterial(hybrid, material.value);
    density.value = object.density.toFixed(0);
    friction.value = object.friction.toFixed(2);
    showMass();
  };
  const onDensity = () => {
    if (!density.checkValidity() || density.value === "") {
      return;
    }
    object.setDensity(hybrid, density.valueAsNumber);
    // The number no longer belongs to a named material, and the object says so.
    material.value = object.material ?? "";
    showMass();
  };
  const onFriction = () => {
    if (!friction.checkValidity() || friction.value === "") {
      return;
    }
    object.setFriction(hybrid, friction.valueAsNumber);
    material.value = object.material ?? "";
  };
  material.addEventListener("change", onMaterial);
  density.addEventListener("input", onDensity);
  friction.addEventListener("input", onFriction);

  // The object's mesh, which is what the gizmo is put on. It is named the way
  // `addArticulatedVisuals` named it -- the body's frame and the visual's index,
  // and an object has exactly one visual.
  const mesh = simulator.meshes.get(`${object.frame}-0`);

  const graphics = simulator.graphics;
  const gizmo = new TransformControls(
    graphics.camera,
    graphics.renderer.domElement,
  );
  if (mesh) {
    gizmo.attach(mesh);
  }
  graphics.scene.add(gizmo);

  // Orbiting and dragging both start with a press on the same canvas, so only
  // one of them can be listening at a time.
  // Read off the control rather than out of the event: the event carries the
  // new state in a `value` field that @types/three does not declare, and the
  // control's own flag is the same answer with a type.
  const onDragging = () => {
    graphics.controls.enabled = !gizmo.dragging;
  };
  // Every drag goes straight into the body. Nothing is stepping while this is
  // up, so this is not a simulation input -- it is what keeps the answer in
  // wasm identical to the one on screen, whichever way the panel is left.
  const onChange = () => {
    if (!mesh) {
      return;
    }
    object.setPose(
      hybrid,
      mesh.position.x,
      mesh.position.y,
      mesh.position.z,
      mesh.quaternion.x,
      mesh.quaternion.y,
      mesh.quaternion.z,
      mesh.quaternion.w,
    );
  };
  gizmo.addEventListener("dragging-changed", onDragging);
  gizmo.addEventListener("objectChange", onChange);

  const setMode = (mode: "translate" | "rotate") => {
    gizmo.setMode(mode);
    move.setAttribute("aria-pressed", String(mode === "translate"));
    rotate.setAttribute("aria-pressed", String(mode === "rotate"));
  };
  const onMove = () => setMode("translate");
  const onRotate = () => setMode("rotate");
  move.addEventListener("click", onMove);
  rotate.addEventListener("click", onRotate);
  setMode("translate");

  return new Promise((resolve) => {
    const close = (placed: boolean) => {
      panel.hidden = true;
      material.removeEventListener("change", onMaterial);
      density.removeEventListener("input", onDensity);
      friction.removeEventListener("input", onFriction);
      move.removeEventListener("click", onMove);
      rotate.removeEventListener("click", onRotate);
      place.removeEventListener("click", onPlace);
      discard.removeEventListener("click", onDiscard);

      gizmo.removeEventListener("dragging-changed", onDragging);
      gizmo.removeEventListener("objectChange", onChange);
      gizmo.detach();
      graphics.scene.remove(gizmo);
      gizmo.dispose();
      // Whatever the drag turned off, since the two share the canvas.
      graphics.controls.enabled = true;

      resolve(placed);
    };
    // Nothing left to collect: the pose went in as it was dragged and the
    // material as it was chosen.
    const onPlace = () => close(true);
    const onDiscard = () => close(false);

    place.addEventListener("click", onPlace);
    discard.addEventListener("click", onDiscard);
    panel.hidden = false;
  });
}
